import { api } from './api';
import {
  ListQuery,
  ManyEnvelope,
  OneEnvelope,
  listQueryToParams,
} from '../types/api';

/**
 * Generic CRUD service bound to a backend resource path. Used by
 * `useCrudResource` and module-level pages to avoid one-off axios calls.
 *
 * Mirrors the response shapes emitted by `BaseController` in
 * `backend/app/src/core/base/`.
 */
/**
 * Per-request options. `tenantId` overrides the active-customer header for
 * this call: a number scopes to that tenant, `'*'` requests cross-tenant
 * scope over every tenant the user can access.
 */
export interface RequestOpts {
  tenantId?: number | '*';
}

function optsToConfig(opts?: RequestOpts) {
  return opts?.tenantId !== undefined
    ? { headers: { 'X-Tenant-Id': String(opts.tenantId) } }
    : {};
}

export class CrudService<T, TCreate = Partial<T>, TUpdate = Partial<T>> {
  constructor(private readonly path: string) {}

  async list(query?: ListQuery, opts?: RequestOpts): Promise<ManyEnvelope<T>> {
    const res = await api.get<ManyEnvelope<T>>(this.path, {
      params: listQueryToParams(query),
      ...optsToConfig(opts),
    });
    return res.data;
  }

  async get(id: number | string, include?: string[], opts?: RequestOpts): Promise<T> {
    const res = await api.get<OneEnvelope<T>>(`${this.path}/${id}`, {
      params: include?.length ? { include: include.join(',') } : undefined,
      ...optsToConfig(opts),
    });
    return res.data.data;
  }

  async create(payload: TCreate): Promise<T> {
    const res = await api.post<OneEnvelope<T>>(this.path, payload);
    return res.data.data;
  }

  async update(id: number | string, payload: TUpdate, opts?: RequestOpts): Promise<T> {
    const res = await api.put<OneEnvelope<T>>(`${this.path}/${id}`, payload, optsToConfig(opts));
    return res.data.data;
  }

  async remove(id: number | string, opts?: RequestOpts): Promise<void> {
    await api.delete(`${this.path}/${id}`, optsToConfig(opts));
  }
}
